import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  CircularProgress,
  alpha,
  Chip,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../../lib/axios';
import type { ResellerProfile, TierName } from '../../../types/b2b';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

interface ResellerSelectorProps {
  selectedReseller: ResellerProfile | null;
  onResellerSelect: (reseller: ResellerProfile | null) => void;
  onCreateClick?: () => void;
  colors: POSColors;
  disabled?: boolean;
}

// Special option for creating a new reseller
const CREATE_RESELLER_OPTION = {
  id: '__create__',
  companyName: 'Create',
  isCreateOption: true,
} as ResellerProfile & { isCreateOption: boolean };

// Get tier-specific color
const getTierColor = (tier: TierName | undefined): string => {
  switch (tier) {
    case 'bronze':
      return '#CD7F32';
    case 'silver':
      return '#C0C0C0';
    case 'gold':
      return '#FFD700';
    case 'platinum':
      return '#E5E4E2';
    case 'custom':
      return '#7C3AED';
    default:
      return '#9CA3AF';
  }
};

export const ResellerSelector: React.FC<ResellerSelectorProps> = ({
  selectedReseller,
  onResellerSelect,
  onCreateClick,
  colors,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Fetch resellers based on search term
  const { data: resellersData, isLoading } = useQuery({
    queryKey: ['b2b-resellers', searchTerm],
    queryFn: async () => {
      const response = await axiosInstance.get('/reseller-profiles', {
        params: {
          isApproved: 'true',
        },
      });
      const resellers = Array.isArray(response.data) ? response.data : response.data.data || [];

      // Filter by search term if provided
      if (searchTerm && searchTerm.length >= 1) {
        const term = searchTerm.toLowerCase();
        return resellers.filter((reseller: ResellerProfile) => {
          const companyMatch = reseller.companyName?.toLowerCase().includes(term);
          const userNameMatch = reseller.user
            ? `${reseller.user.firstName} ${reseller.user.lastName}`.toLowerCase().includes(term)
            : false;
          const emailMatch = reseller.user?.email?.toLowerCase().includes(term);
          return companyMatch || userNameMatch || emailMatch;
        });
      }

      return resellers;
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  const resellers = resellersData || [];

  // Build options: resellers + create option
  const options = [
    ...resellers,
    ...(onCreateClick && searchTerm.length > 0 ? [CREATE_RESELLER_OPTION] : []),
  ];

  // Debounce timeout ref
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    setInputValue(value);
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // Debounce the search
    debounceRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = (
    _event: React.SyntheticEvent,
    value: (ResellerProfile & { isCreateOption?: boolean }) | null
  ) => {
    if (value && 'isCreateOption' in value && value.isCreateOption) {
      onCreateClick?.();
      setInputValue('');
      setSearchTerm('');
    } else {
      onResellerSelect(value);
      if (value) {
        setInputValue(value.companyName);
      }
    }
  };

  const handleClear = () => {
    onResellerSelect(null);
    setInputValue('');
    setSearchTerm('');
  };

  const getResellerDisplayName = (reseller: ResellerProfile): string => {
    if (reseller.user) {
      return `${reseller.companyName} (${reseller.user.firstName} ${reseller.user.lastName})`;
    }
    return reseller.companyName;
  };

  const getTierDisplayName = (reseller: ResellerProfile): string | null => {
    if (reseller.defaultTier?.tierName) {
      return reseller.defaultTier.tierName.toUpperCase();
    }
    return null;
  };

  // If reseller is selected, show selected state
  if (selectedReseller) {
    const tierName = selectedReseller.defaultTier?.tierName;
    const tierColor = getTierColor(tierName);
    const tierLabel = getTierDisplayName(selectedReseller);

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          backgroundColor: alpha(colors.accent, 0.1),
          borderRadius: '10px',
          border: `1px solid ${alpha(colors.accent, 0.3)}`,
        }}
      >
        <StorefrontIcon sx={{ color: colors.accent, fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: colors.textPrimary,
              }}
            >
              {selectedReseller.companyName}
            </Typography>
            {tierLabel && (
              <Chip
                label={tierLabel}
                size="small"
                sx={{
                  height: '18px',
                  fontSize: '10px',
                  fontWeight: 600,
                  backgroundColor: alpha(tierColor, 0.15),
                  color: tierColor,
                  border: `1px solid ${tierColor}`,
                  '& .MuiChip-label': {
                    px: 0.75,
                  },
                }}
              />
            )}
          </Box>
          {selectedReseller.user && (
            <Typography
              sx={{
                fontSize: '11px',
                color: colors.textMuted,
              }}
            >
              {selectedReseller.user.firstName} {selectedReseller.user.lastName}
              {selectedReseller.user.email && ` • ${selectedReseller.user.email}`}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={handleClear}
          disabled={disabled}
          aria-label="clear reseller"
          sx={{
            color: colors.textMuted,
            '&:hover': {
              color: colors.textPrimary,
              backgroundColor: alpha(colors.textMuted, 0.1),
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  // Default state: show search autocomplete
  return (
    <Box>
      {/* No Reseller Label */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        <StorefrontIcon sx={{ color: colors.textMuted, fontSize: 18 }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: colors.textMuted,
          }}
        >
          Select Reseller
        </Typography>
      </Box>

      {/* Reseller Search Autocomplete */}
      <Autocomplete
        disabled={disabled}
        options={options}
        getOptionLabel={(option) => {
          if ('isCreateOption' in option && option.isCreateOption) {
            return 'Create Reseller';
          }
          return getResellerDisplayName(option);
        }}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={isLoading}
        noOptionsText={
          searchTerm.length > 0 ? 'No resellers found' : 'Type to search resellers'
        }
        filterOptions={(x) => x} // Disable client-side filtering, we filter ourselves
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search reseller..."
            size="small"
            label="Reseller"
            InputLabelProps={{ shrink: true, sx: { display: 'none' } }}
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Search reseller',
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surface,
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  borderColor: colors.border,
                },
                '&:hover fieldset': {
                  borderColor: colors.textMuted,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.accent,
                  borderWidth: 2,
                },
              },
              '& input': {
                color: colors.textPrimary,
                '&::placeholder': {
                  color: colors.textMuted,
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => {
          // Create Reseller option
          if ('isCreateOption' in option && option.isCreateOption) {
            return (
              <li {...props} key="create-reseller">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    color: colors.accent,
                  }}
                >
                  <PersonAddIcon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                    Create Reseller
                  </Typography>
                </Box>
              </li>
            );
          }

          // Regular reseller option
          const tierName = option.defaultTier?.tierName;
          const tierColor = getTierColor(tierName);
          const tierLabel = option.defaultTier?.tierName?.toUpperCase();

          return (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: colors.textPrimary,
                    }}
                  >
                    {option.companyName}
                  </Typography>
                  {tierLabel && (
                    <Chip
                      label={tierLabel}
                      size="small"
                      sx={{
                        height: '16px',
                        fontSize: '9px',
                        fontWeight: 600,
                        backgroundColor: alpha(tierColor, 0.15),
                        color: tierColor,
                        border: `1px solid ${tierColor}`,
                        '& .MuiChip-label': {
                          px: 0.5,
                        },
                      }}
                    />
                  )}
                </Box>
                {option.user && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: colors.textMuted,
                    }}
                  >
                    {option.user.firstName} {option.user.lastName}
                    {option.user.email && ` • ${option.user.email}`}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        ListboxProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            mt: 0.5,
            '& .MuiAutocomplete-option': {
              '&:hover': {
                backgroundColor: alpha(colors.accent, 0.1),
              },
              '&[aria-selected="true"]': {
                backgroundColor: alpha(colors.accent, 0.15),
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default ResellerSelector;
